// Bottom sheet that slides up (snappy spring); a plain fade in the Still tier.
// Only the sheet takes part in the exit animation. The scrim is a plain element (CSS fade-in) that
// leaves with the sheet: as a second animated sibling, its exit could be dropped when the app
// re-rendered mid-close (a tier change after slow frames), leaving it covering the app.
import { motion, useDragControls, useIsPresent } from 'motion/react'
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import { springs } from '../motion/springs'
import { useMotionTier } from '../motion/tier'
import { closeOverlay } from './router'

const FADE_OUT = { opacity: 0 }
const FADE_IN = { opacity: 1 }
const BELOW = { y: '100%' }
const UP = { y: 0 }
const STILL_T = { duration: 0.15 }
/** The sheet only moves down when dragged, following the finger, and springs back unless it closes. */
const DRAG_BOX = { top: 0, bottom: 0 }
const DRAG_GIVE = { top: 0, bottom: 1 }

// ---------- modal behaviour (WI-28, L6-01) ----------
// Every sheet and full-screen dialog: focus moves in when it opens, Tab stays inside it, Escape
// closes it (like ✕ or Back), the page behind is `inert`, and focus goes back to whatever opened
// it. Open dialogs form a stack; only the top one handles keys and decides what's inert. A sheet
// leaving (its exit animation still running) is already off the stack.

interface Open {
  el: HTMLElement
  modal: boolean
  opener: Element | null
  closing: boolean
  close(): void
}

const stack: Open[] = []
const madeInert = new Set<HTMLElement>()
/** Part of the open dialog rendered elsewhere (a portal, e.g. the session's footer buttons). */
const PART = '[data-modal-part]'
// The scrim stays tappable; toasts and other live regions stay announced.
const KEEP = `.scrim, [role="status"], [role="alert"], [aria-live], ${PART}`
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'

// Something mounted beside an open dialog later (the lazily loaded 3D stage under a deep-linked
// sheet) is made inert too.
let watcher: MutationObserver | null = null

function syncInert() {
  watcher?.disconnect()
  for (const el of madeInert) el.inert = false
  madeInert.clear()
  const top = stack.at(-1)
  if (!top?.modal) return
  watcher ??= new MutationObserver(syncInert)
  // Everything beside the dialog and beside each of its ancestors, up to <body>.
  for (let node: HTMLElement | null = top.el; node && node !== document.body; node = node.parentElement) {
    if (node.parentElement) watcher.observe(node.parentElement, { childList: true })
    for (const sib of node.parentElement?.children ?? []) {
      if (sib === node || !(sib instanceof HTMLElement) || sib.inert || sib.matches(KEEP)) continue
      sib.inert = true
      madeInert.add(sib)
    }
  }
}

function focusables(el: HTMLElement): HTMLElement[] {
  const roots = [el, ...document.querySelectorAll<HTMLElement>(PART)]
  return roots.flatMap((r) => [...r.querySelectorAll<HTMLElement>(FOCUSABLE)]).filter((f) => f.getClientRects().length > 0 && !f.closest('[inert]'))
}

const inside = (top: Open, at: Element | null) => !!at && (top.el.contains(at) || !!at.closest(PART))

function onKey(e: KeyboardEvent) {
  const top = stack.at(-1)
  if (!top) return
  if (e.key === 'Escape') {
    if (e.defaultPrevented || top.closing) return
    e.preventDefault()
    top.closing = true
    top.close()
    return
  }
  if (e.key !== 'Tab' || !top.modal) return
  const f = focusables(top.el)
  const first = f[0]
  const last = f.at(-1)
  const at = document.activeElement
  if (!first || !last) {
    e.preventDefault()
    top.el.focus({ preventScroll: true })
  } else if (!inside(top, at)) {
    e.preventDefault()
    ;(e.shiftKey ? last : first).focus()
  } else if (e.shiftKey && (at === first || at === top.el)) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && at === last) {
    e.preventDefault()
    first.focus()
  }
}

/**
 * Dialog behaviour for `ref` (give the element `tabIndex={-1}` so it can take focus).
 * `onClose` is what Escape does (default: close the top overlay, like ✕). `modal: false` keeps the
 * page behind usable (no inert, no trap) but still moves focus in, closes on Escape and returns
 * focus. `active: false` takes it off the stack (a sheet on its way out).
 */
export function useModal(ref: RefObject<HTMLElement | null>, { onClose = closeOverlay, modal = true, active = true }: { onClose?(): void; modal?: boolean; active?: boolean } = {}) {
  const close = useRef(onClose)
  useEffect(() => {
    close.current = onClose
  })
  useEffect(() => {
    const el = ref.current
    if (!el || !active) return
    const entry: Open = { el, modal, opener: document.activeElement, closing: false, close: () => close.current() }
    if (!stack.length) document.addEventListener('keydown', onKey)
    stack.push(entry)
    syncInert()
    if (!el.contains(document.activeElement)) el.focus({ preventScroll: true })
    return () => {
      const i = stack.indexOf(entry)
      const wasTop = i === stack.length - 1
      if (i >= 0) stack.splice(i, 1)
      if (!stack.length) document.removeEventListener('keydown', onKey)
      syncInert()
      // Back to the opener, unless another dialog has opened on top meanwhile.
      const back = entry.opener
      if (wasTop && back instanceof HTMLElement && back !== document.body && back.isConnected && !back.closest('[inert]')) back.focus({ preventScroll: true })
    }
  }, [ref, modal, active])
}

/** A drag down the header this far (px), or a flick this fast (px/s), closes the sheet. */
const DRAG_CLOSE_PX = 90
const DRAG_CLOSE_V = 600

/**
 * One sheet for the whole app, with one header (WI-40 #6): the drag handle, the title and ✕ sit in
 * a header that stays at the top while the sheet scrolls, inside the sheet's edge. Dragging the
 * header down closes the sheet (not in Still, where the sheet only fades).
 */
export function Sheet({ title, children, label, kicker, compact }: { title: string; children: ReactNode; label?: string; kicker?: string; compact?: boolean }) {
  const tier = useMotionTier()
  const [still] = useState(() => tier === 'still')
  const ref = useRef<HTMLElement>(null)
  const headId = useId()
  const drag = useDragControls()
  useModal(ref, { active: useIsPresent() })
  return (
    <>
      <div className="scrim" onClick={closeOverlay} />
      <motion.section
        ref={ref}
        className="sheet"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={label}
        aria-labelledby={label ? undefined : headId}
        initial={still ? FADE_OUT : BELOW}
        animate={still ? FADE_IN : UP}
        exit={still ? FADE_OUT : BELOW}
        transition={still ? STILL_T : springs.snappy}
        drag={still ? false : 'y'}
        dragControls={drag}
        dragListener={false}
        dragConstraints={DRAG_BOX}
        dragElastic={DRAG_GIVE}
        dragSnapToOrigin
        onDragEnd={(_, info) => {
          if (info.offset.y > DRAG_CLOSE_PX || info.velocity.y > DRAG_CLOSE_V) closeOverlay()
        }}
      >
        <header
          className="sheet-head"
          onPointerDown={(e) => {
            // The ✕ is a button, not a handle.
            if (!still && !(e.target as Element).closest('button')) drag.start(e)
          }}
        >
          <div className="grab" aria-hidden />
          {/* WI-42: a step sheet carries a small line over a compact title ("Trip setup · 1 of 2"). */}
          <div className={`screen-head${compact ? ' compact' : ''}`}>
            {kicker ? (
              <div className="sheet-title">
                <p className="mono sheet-kicker">{kicker}</p>
                <h2 className="h1" id={headId}>
                  {title}
                </h2>
              </div>
            ) : (
              <h2 className="h1" id={headId}>
                {title}
              </h2>
            )}
            <button className="x-btn press" aria-label="Close" onClick={closeOverlay}>
              ✕
            </button>
          </div>
        </header>
        {children}
      </motion.section>
    </>
  )
}
